"use client"

import {Button} from "@/components/ui/button"
import {Input} from "@/components/ui/input"
import {FieldErrors, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from "@/components/ui/form";
import { z } from "@/lib/zod";
import React, {useState} from "react";
import {env} from "next-runtime-env";
import {trackLead} from "@/lib/analytics";
import {Loader} from "lucide-react";
import {messageForFailedResponse, NETWORK_ERROR_MESSAGE} from "@/lib/form-errors";


const formSchema = z.object({
    email: z.string().email("This is not a valid email"),
})

type inferMarketUpdateType = z.infer<typeof formSchema>;

export default function NewsLetter() {

    const apiUri = env('NEXT_PUBLIC_API_URI');
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [buttonIsLoading, setButtonIsLoading] = useState<boolean | undefined>()

    const methods = useForm<inferMarketUpdateType>({
        resolver: zodResolver(formSchema),
        defaultValues:
            {
                email: '',
            },
        reValidateMode: "onBlur",
        mode: "onSubmit",
    });

    const {
        register,
        handleSubmit,
        reset,
        control,
    } = methods;

    const onError = (errors: FieldErrors) => {
        console.error("Validation errors:", errors);
    };

    async function onSubmit(data: inferMarketUpdateType) {
        setButtonIsLoading(true);
        setErrorMessage("");
        setSuccessMessage("");
        try {
            const res = await fetch(`${apiUri}/api/v1/leads/marketupdate`, {
                method: 'POST',
                body: JSON.stringify(data),
                headers: {'Content-Type': 'application/json',},
            });
            if (!res.ok) {
                setErrorMessage(await messageForFailedResponse(res));
                return;
            }
            trackLead("newsletter_signup");
            setSuccessMessage("Thank you for subscribing to our newsletter!");
            reset();

            // Set a timer to hide the success message after 10 seconds
            setTimeout(() => {
                setSuccessMessage("");
            }, 10000); // 10000 ms = 10 seconds
        } catch {
            setErrorMessage(NETWORK_ERROR_MESSAGE);
        } finally {
            setButtonIsLoading(false);
        }
    }

    return (
        <Form {...methods}>
            <form onSubmit={handleSubmit(onSubmit, onError)}>
                <div className="w-full bg-zinc-200">
                    <div className="flex flex-col h-56 items-center justify-evenly">
                        <div className="flex px-4 text-center text-lg md:text-2xl font-thin">
                            Subscribe to receive our monthly market update!
                        </div>
                        <div className="flex rounded outline-white-500 w-full max-w-96 px-4">
                            <FormField
                                control={control}
                                name="email"
                                render={() => (
                                    <FormItem>
                                        <FormControl>
                                            <div className="flex flex-nowrap w-full max-w-96">
                                                <Input
                                                    {...register("email")}
                                                    placeholder="Email"
                                                    className="rounded-r-none bg-white placeholder:text-zinc-400"
                                                />
                                                <Button
                                                    type="submit"
                                                    disabled={buttonIsLoading}
                                                    variant="brand"
                                                    className="rounded-l-none"
                                                >
                                                    {buttonIsLoading ? (
                                                        <Loader className="animate-spin"/> // Spinner icon
                                                    ) : (
                                                        "Subscribe"
                                                    )}
                                                </Button>
                                            </div>
                                        </FormControl>
                                        {successMessage ? (
                                            <span role="status" className="text-green-500 text-base">
                                {successMessage}
                            </span>
                                        ) : errorMessage ? (
                                            <p role="alert" className="max-w-96 text-sm text-red-600">
                                                {errorMessage}
                                            </p>
                                        ) : (
                                            <FormMessage className="text-red-300"/>
                                        )}
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                </div>
            </form>
        </Form>
    );
}